import React, { useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import {
    DataGridPremium,
    GridColDef,
    useGridApiRef,
    GridToolbar,
} from "@mui/x-data-grid-premium";
import dayjs from "dayjs";
import FastAPIClient from "../../client";
import config from "../../config";
import { frFR } from "@mui/x-data-grid/locales";

const client = new FastAPIClient(config);

type TransactionSummary = {
    year: number;
    brand: string;
    product_name: string;
    total_volume: number;
    distinct_clients: number;
};

type ProcessedBrandProductData = {
    id: string; // Unique ID for each row (e.g., combination of brand and product)
    brand: string;
    product_name: string;
    volume_current_year: number;
    volume_previous_year: number;
    clients_current_year: number;
    clients_previous_year: number;
};

const BrandTable: React.FC = () => {
    const [processedData, setProcessedData] = useState<
        ProcessedBrandProductData[] | null
    >(null);
    const [loading, setLoading] = useState<boolean>(false);

    const apiRef = useGridApiRef();

    const fetchTransactionData = async () => {
        setLoading(true);
        try {
            const response = await client.fetchBrandStats();
            const fetchedData: TransactionSummary[] = response.results || [];

            // Process data to group by brand and product and separate by year
            const dataByBrandProduct: {
                [key: string]: ProcessedBrandProductData;
            } = {};
            const currentYear = new Date().getFullYear();
            const previousYear = currentYear - 1;

            fetchedData.forEach((item) => {
                const key = `${item.brand}-${item.product_name}`;
                if (!dataByBrandProduct[key]) {
                    dataByBrandProduct[key] = {
                        id: key,
                        brand: item.brand,
                        product_name: item.product_name,
                        volume_current_year: 0,
                        volume_previous_year: 0,
                        clients_current_year: 0,
                        clients_previous_year: 0,
                    };
                }

                if (item.year === currentYear) {
                    dataByBrandProduct[key].volume_current_year =
                        item.total_volume;
                    dataByBrandProduct[key].clients_current_year =
                        item.distinct_clients;
                } else if (item.year === previousYear) {
                    dataByBrandProduct[key].volume_previous_year =
                        item.total_volume;
                    dataByBrandProduct[key].clients_previous_year =
                        item.distinct_clients;
                }
            });

            const sortedData = Object.values(dataByBrandProduct);

            // Sort data: null brands at the bottom, then by brand, then by product_name
            sortedData.sort((a, b) => {
                // Rule 1: Null or undefined brands go to the bottom
                if (a.brand == null && b.brand != null) {
                    return 1; // a comes after b
                }
                if (a.brand != null && b.brand == null) {
                    return -1; // a comes before b
                }

                // Rule 2: If both brands are non-empty (neither null nor undefined), sort alphabetically by brand
                if (a.brand != null && b.brand != null) {
                    const brandA = a.brand.toLowerCase();
                    const brandB = b.brand.toLowerCase();
                    if (brandA < brandB) return -1;
                    if (brandA > brandB) return 1;
                }
                // If brands are effectively the same (both null/undefined or same non-empty after primary sort)
                // Rule 3: Sort by product_name alphabetically
                const productNameA = a.product_name.toLowerCase();
                const productNameB = b.product_name.toLowerCase();
                if (productNameA < productNameB) return -1;
                if (productNameA > productNameB) return 1;

                return 0; // Should not be reached if product_names are unique for a brand or both brands are null
            });

            setProcessedData(sortedData);
        } catch (err) {
            console.error("Failed to fetch transaction summary", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTransactionData();
    }, []);

    const columns: GridColDef[] = [
        { field: "brand", headerName: "Marque", flex: 1 },
        { field: "product_name", headerName: "Produit", flex: 2 },
        {
            field: "volume_previous_year",
            headerName: `Volume ${new Date().getFullYear() - 1} (L)`,
            type: "number",
            flex: 1,
        },
        {
            field: "volume_current_year",
            headerName: `Volume ${new Date().getFullYear()} (L)`,
            type: "number",
            flex: 1,
        },
        {
            field: "clients_previous_year",
            headerName: `# Clients ${new Date().getFullYear() - 1}`,
            type: "number",
            flex: 1,
        },

        {
            field: "clients_current_year",
            headerName: `# Clients ${new Date().getFullYear()}`,
            type: "number",
            flex: 1,
        },
    ];

    return (
        <Box mt={4} style={{ height: 600, width: "100%" }}>
            {loading ? (
                <CircularProgress />
            ) : (
                <DataGridPremium
                    rows={processedData || []}
                    columns={columns}
                    showToolbar
                    apiRef={apiRef}
                    initialState={{
                        rowGrouping: {
                            model: ["brand"],
                        },
                        columns: {
                            columnVisibilityModel: {
                                brand: false,
                                product_name: true,
                            },
                        },
                    }}
                    rowGroupingColumnMode="multiple"
                    disableRowSelectionOnClick
                    localeText={
                        frFR.components.MuiDataGrid.defaultProps.localeText
                    }
                    slots={{ toolbar: GridToolbar }}
                    slotProps={{
                        toolbar: {
                            csvOptions: {
                                fileName: `Transactions_${dayjs().format(
                                    "YYYY-MM-DD"
                                )}`,
                            },
                            excelOptions: {
                                fileName: `Transactions_${dayjs().format(
                                    "YYYY-MM-DD"
                                )}`,
                            },
                        },
                    }}
                    sx={{
                        "& .MuiButton-root": {
                            color: "#000000",
                            "&:hover": {
                                backgroundColor: "#222",
                            },
                        },
                        "& .MuiSelect-root, & .MuiInputBase-root, & .MuiOutlinedInput-root":
                            {
                                color: "#fff",
                                "& .MuiSvgIcon-root": {
                                    color: "#fff",
                                },
                            },
                        "& .MuiInputBase-input": {
                            color: "#fff",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#000",
                        },
                        "& .MuiDataGrid-toolbarContainer": {
                            gap: 1,
                        },
                    }}
                />
            )}
        </Box>
    );
};

export default BrandTable;
